import { useEffect, useRef, useState, type JSX } from 'react';
import type { PropertyDescriptor } from '../../../core/definitions';
import type { Id, SettingsBag } from '../../../core/model';
import { Dialog } from '../../components/Dialog';
import { ViewCanvas } from '../../components/ViewCanvas';
import { useProject, useRuntime, useStudio } from '../../context';
import { useEditSession } from '../../hooks/useEditSession';
import { attempt, errorMessage, safely } from '../../safe';
import { changedKeys, restorePatch } from './coerce';
import { MediaTransport } from './MediaTransport';
import { PropertyForm } from './PropertyForm';
import { BrowserProperties } from './BrowserProperties';
import '../dialogs.css';

/** Descriptors for the current settings; a throwing plugin definition becomes an inline error. */
export function describe(build: () => PropertyDescriptor[]): { descriptors: PropertyDescriptor[]; error: string | null } {
  try {
    return { descriptors: build(), error: null };
  } catch (error) {
    return { descriptors: [], error: errorMessage(error) };
  }
}

/**
 * Source properties. Edits reach the source live (or on commit, per
 * descriptor) so the preview shows them, but stay out of history while the
 * dialog is open: OK records everything as one undo step, and Cancel, Escape
 * or the close button put back the settings captured when it opened without
 * recording anything.
 */
export function PropertiesDialog({ sourceId, onClose }: { sourceId: Id; onClose(): void }): JSX.Element | null {
  const studio = useStudio();
  const { commands, registry } = studio;
  const source = useProject((p) => p.sources[sourceId]);
  const status = useRuntime((r) => r.sources[sourceId]?.status);
  const hasVideo = useRuntime((r) => r.sources[sourceId]?.hasVideo);
  const live = useRuntime((r) => r.sources[sourceId]?.live);
  const session = useEditSession(studio);
  const captured = useRef<SettingsBag | null>(null);
  if (captured.current === null && source) captured.current = structuredClone(source.settings);
  const [invalid, setInvalid] = useState(false);
  const [interactionDetached,setInteractionDetached]=useState(false);

  const exists = source !== undefined;
  useEffect(() => {
    if (!exists) onClose();
  }, [exists, onClose]);
  if (!source) return null;

  const def = registry.getSource(source.kind);
  const { descriptors, error } = describe(() => (def ? def.properties(source.settings, status) : []));

  const read = (key: string) => (): unknown => studio.project.getState().project.sources[sourceId]?.settings[key];
  const write =
    (key: string) =>
    (next: unknown, options?: Parameters<typeof commands.updateSourceSettings>[2]): void =>
      commands.updateSourceSettings(sourceId, { [key]: next }, options);
  const change = (key: string, value: unknown): void => session.edit(key, read(key), write(key), value);

  const ok = (): void => {
    session.commit();
    onClose();
  };

  const cancel = (): void => {
    const current = studio.project.getState().project.sources[sourceId];
    if (current && captured.current && changedKeys(captured.current, current.settings).length > 0) {
      // The session's pending entries now match their start values, so closing records nothing.
      attempt(studio, () => commands.updateSourceSettings(sourceId, restorePatch(captured.current!, current.settings), { undoable: false }));
    }
    onClose();
  };

  const defaults = (): void => {
    if (!def) return;
    const values = def.defaults();
    for (const key of Object.keys(values)) change(key, values[key]);
  };

  const retry = def?.caps.retry === true;
  const showPreview = hasVideo ?? def?.caps.video ?? false;
  return (
    <Dialog
      title={`Properties for "${source.name}"`}
      onClose={cancel}
      width={640}
      height={showPreview ? 640 : undefined}
      flush
      suspended={interactionDetached}
      footer={
        <>
          <button type="button" className="ui-btn" style={{ marginRight: 'auto' }} disabled={!def} onClick={defaults}>
            Defaults
          </button>
          {invalid ? <span className="ui-error-text">Fix the highlighted field to keep it.</span> : null}
          <button type="button" className="ui-btn" onClick={cancel}>
            Cancel
          </button>
          <button type="button" className="ui-btn primary" onClick={ok}>
            OK
          </button>
        </>
      }
    >
      <div className="dialog-column">
        {showPreview ? <ViewCanvas target={{ type: 'source', sourceId }} label={`${source.name} preview`} className="dialog-preview" /> : null}
        <div className="dialog-scroll">
          {status?.state === 'error' ? (
            <div className="ui-row ui-error-text" role="alert" style={{ marginBottom: 8 }}>
              <span className="ui-grow ui-selectable">{status.error?.message ?? 'The source reported an error.'}</span>
              {retry ? (
                <button type="button" className="ui-btn" onClick={() => void safely(studio, () => studio.invokeSource(sourceId, 'retry'))}>
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}
          {def?.caps.controllableMedia ? <MediaTransport sourceId={sourceId} /> : null}
          {!def ? <div className="ui-warn-text">The source type "{source.kind}" is not available (is its plugin loaded?).</div> : null}
          {error ? <div className="ui-error-text">Could not build the settings form: {error}</div> : null}
          {def && !error ? (
            <PropertyForm
              descriptors={descriptors}
              settings={source.settings}
              onChange={change}
              onCommit={() => undefined}
              onAction={(action) => void safely(studio, () => studio.invokeSource(sourceId, action))}
              live={live}
              onValidityChange={setInvalid}
            />
          ) : null}
          {source.kind === 'browser' ? <BrowserProperties sourceId={sourceId} settings={source.settings} onDetachedChange={setInteractionDetached}/> : null}
        </div>
      </div>
    </Dialog>
  );
}
